import {GLOW_THREAD_TEXTURE_KEYS} from '../../configs/glowingThreads';

/**
 * Текстуры светящихся нитей (см. configs/glowingThreads): пиксели считаются кодом
 * и регистрируются через textures.addUint8Array. Все белые, цвет задает tint.
 * Полосы растягиваются вдоль нити, поэтому важен только их поперечный профиль.
 */

export const GLOW_THREAD_TEXTURE_SIZES = {
  [GLOW_THREAD_TEXTURE_KEYS.CORE]: {width: 4, height: 32},
  [GLOW_THREAD_TEXTURE_KEYS.HALO]: {width: 4, height: 64},
  [GLOW_THREAD_TEXTURE_KEYS.NODE]: {width: 64, height: 64},
  [GLOW_THREAD_TEXTURE_KEYS.ARROW]: {width: 64, height: 64},
  [GLOW_THREAD_TEXTURE_KEYS.STRIKE]: {width: 64, height: 64},
};

const clamp01 = (value) => Math.min(1, Math.max(0, value));

const buildPixels = (key, resolveAlpha) => {
  const {width, height} = GLOW_THREAD_TEXTURE_SIZES[key];
  const pixels = new Uint8Array(width * height * 4);
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const offset = (y * width + x) * 4;
      pixels[offset] = 255;
      pixels[offset + 1] = 255;
      pixels[offset + 2] = 255;
      pixels[offset + 3] = Math.round(255 * clamp01(resolveAlpha(x, y, width, height)));
    }
  }
  return pixels;
};

// Расстояние от середины полосы поперек: 0 — середина, 1 — край картинки
const acrossStrip = (y, height) => Math.abs(y + 0.5 - height / 2) / (height / 2);

/** Сердцевина: плотная середина и сглаженный край, по краю картинки прозрачна */
export const buildGlowThreadCorePixels = () => buildPixels(
  GLOW_THREAD_TEXTURE_KEYS.CORE,
  (x, y, width, height) => 1 - clamp01((acrossStrip(y, height) - 0.6) / 0.35),
);

/** Ореол: мягко гаснет от середины к краям */
export const buildGlowThreadHaloPixels = () => buildPixels(
  GLOW_THREAD_TEXTURE_KEYS.HALO,
  (x, y, width, height) => (1 - clamp01(acrossStrip(y, height) / 0.95)) ** 1.5,
);

/** Точка: плотный круг с мягким краем */
export const buildGlowThreadNodePixels = () => buildPixels(
  GLOW_THREAD_TEXTURE_KEYS.NODE,
  (x, y, width, height) => {
    const distance = Math.hypot(x + 0.5 - width / 2, y + 0.5 - height / 2) / (width / 2);
    return 1 - clamp01((distance - 0.86) / 0.1);
  },
);

// Стрелка в долях половины стороны текстуры: острие справа
const ARROW_SHAPE = {tipX: 0.8, headBaseX: 0.04, headHalfWidth: 0.72, tailX: -0.8, shaftHalfWidth: 0.27};
const COVERAGE_SUBSAMPLES = 4;
// Черта в тех же долях: полоса во всю длину текстуры
const STRIKE_HALF_WIDTH = 0.153;

const checkIfInsideArrow = (u, v) => {
  const {tipX, headBaseX, headHalfWidth, tailX, shaftHalfWidth} = ARROW_SHAPE;
  if (u >= headBaseX) {
    return u <= tipX && Math.abs(v) <= ((tipX - u) / (tipX - headBaseX)) * headHalfWidth;
  }
  return u >= tailX && Math.abs(v) <= shaftHalfWidth;
};

const checkIfInsideStrike = (u, v) => Math.abs(v) <= STRIKE_HALF_WIDTH;

// Фигура по функции попадания: край сглажен подсчетом покрытия пикселя
const buildShapePixels = (textureKey, checkIfInside) => buildPixels(
  textureKey,
  (x, y, width, height) => {
    let covered = 0;
    for (let sy = 0; sy < COVERAGE_SUBSAMPLES; sy += 1) {
      for (let sx = 0; sx < COVERAGE_SUBSAMPLES; sx += 1) {
        const u = ((x + (sx + 0.5) / COVERAGE_SUBSAMPLES) / width) * 2 - 1;
        const v = ((y + (sy + 0.5) / COVERAGE_SUBSAMPLES) / height) * 2 - 1;
        covered += checkIfInside(u, v) ? 1 : 0;
      }
    }
    return covered / (COVERAGE_SUBSAMPLES * COVERAGE_SUBSAMPLES);
  },
);

/** Стрелка транзита */
export const buildGlowThreadArrowPixels = () => buildShapePixels(GLOW_THREAD_TEXTURE_KEYS.ARROW, checkIfInsideArrow);

/** Черта обрыва */
export const buildGlowThreadStrikePixels = () => buildShapePixels(GLOW_THREAD_TEXTURE_KEYS.STRIKE, checkIfInsideStrike);

const PIXEL_BUILDERS = {
  [GLOW_THREAD_TEXTURE_KEYS.CORE]: buildGlowThreadCorePixels,
  [GLOW_THREAD_TEXTURE_KEYS.HALO]: buildGlowThreadHaloPixels,
  [GLOW_THREAD_TEXTURE_KEYS.NODE]: buildGlowThreadNodePixels,
  [GLOW_THREAD_TEXTURE_KEYS.ARROW]: buildGlowThreadArrowPixels,
  [GLOW_THREAD_TEXTURE_KEYS.STRIKE]: buildGlowThreadStrikePixels,
};

/**
 * Зарегистрировать текстуры один раз на сцену
 * @param {Phaser.Scene} scene
 * @param {Object} [options]
 * @param {number} [options.linearFilter] — режим фильтрации LINEAR
 * @returns {boolean} готовы ли текстуры
 */
export const ensureGlowThreadTextures = (scene, {linearFilter} = {}) => {
  const textures = scene?.textures;
  if (!textures?.exists || !textures.addUint8Array) {
    return false;
  }

  return Object.values(GLOW_THREAD_TEXTURE_KEYS).every((key) => {
    if (textures.exists(key)) {
      return true;
    }
    const {width, height} = GLOW_THREAD_TEXTURE_SIZES[key];
    const texture = textures.addUint8Array(key, PIXEL_BUILDERS[key](), width, height);
    if (!texture) {
      return false;
    }
    if (linearFilter !== undefined) {
      texture.setFilter?.(linearFilter);
    }
    return true;
  });
};
